import React, {useCallback, useMemo} from 'react'
import styled, {useTheme} from 'styled-components'
import {Icon, Paragraph} from 'common/components'
import convertUnit from 'lib/unit'
import {
  IMAGE_ASSET,
  WINDOW_MODE_MOBILE_WIDTH,
  WINDOW_MODE_TABLET_WIDTH,
} from 'consts'
import {useTranslation} from 'i18n'
import {
  GiftShopExploreTabView,
  GiftShopRootEmpty,
  GiftShopTemplateMasonry,
} from 'pages'
import {requestData} from 'services'
import {GiftShopContentData} from 'types'
import {useHistory, useRoboyuCustomizationHooks} from 'utils'
import {useWindowMode} from 'windows'
import {GiftShopCollectionResendApprovalBanner} from '../ResendApprovalBanner'
import {GiftShopCollectionContentProps} from './GiftShopCollectionContentProps'

const StyledMasonryContainer = styled.div`
  position: relative;
  height: 100%;
  flex: '1 1 auto';
`

const StyledEmptyContainer = styled.div`
  height: 100%;
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
`

const StyledPackageBannerContainer = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  flex: 1;
  width: 100%;
`

const StyledPackageBanner = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  padding: ${convertUnit(4)} ${convertUnit(8)} ${convertUnit(4)};
  margin: ${convertUnit(8)} ${convertUnit(8)} ${convertUnit(8)} auto;
  gap: ${convertUnit(8)};
  background-color: ${({theme}) => theme.info_1};
  border: 1px solid ${({theme}) => theme.primary_1};
  border-radius: ${convertUnit(28)};
  width: 50%;
  @media (max-width: ${WINDOW_MODE_TABLET_WIDTH}px) {
    margin: ${convertUnit(8)};
    width: 100%;
  }
  @media (max-width: ${WINDOW_MODE_MOBILE_WIDTH}px) {
    margin: ${convertUnit(8)};
    width: 100%;
  }
`

export default function GiftShopCollectionContent({
  packages,
  packageLoading,
  stateCollectionData,
  stateSelect,
  stateSelectMode,
}: GiftShopCollectionContentProps) {
  const theme = useTheme()
  const {translate} = useTranslation()
  const {handleFetchRoboyuCustomizationStatus} = useRoboyuCustomizationHooks()
  const data = stateCollectionData[0]
  const history = useHistory()
  const [select, setSelect] = stateSelect
  const selectMode = stateSelectMode[0]
  const mode = useWindowMode()
  const tabViewVisible = useMemo(
    () => (data.length !== 0 ? !selectMode : false),
    [data.length, selectMode],
  )
  const emptyImg = IMAGE_ASSET('giftshop', 'empty.png')

  const handleLoadData = useCallback(
    async (page: number, limit: number) => {
      const response = await requestData('giftshop_get_collection', {
        useDefaultMessage: true,
        actionType: 'fetch',
        params: {page, limit, status: 'COMPLETED'},
      })
      if (typeof response !== 'string' && response.status === 200) {
        if (page === 1) {
          handleFetchRoboyuCustomizationStatus()
        }
        return response.data.result
      }
      return []
    },
    [handleFetchRoboyuCustomizationStatus],
  )

  const handleRenderPackageBanner = useMemo(
    () =>
      !packageLoading &&
      packages.length > 0 && (
        <StyledPackageBannerContainer>
          <StyledPackageBanner
            onClick={() => {
              history.push('giftshop_package_active', {
                sourceData: packages,
              })
            }}>
            <Icon color="primary_5" type="collection" size={14} />
            <Paragraph
              style={{
                flex: 1,
              }}
              fontWeight="bold"
              color="primary_5">
              {translate('giftShop:packageActiveCount', {
                count: packages.length,
              })}
            </Paragraph>
            <div
              style={{
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'center',
                background: theme.primary_5,
                borderRadius: convertUnit(16),
                paddingLeft: convertUnit(8),
              }}>
              <Paragraph color="white_1">{translate('global:open')}</Paragraph>
              <Icon color="white_1" type="chevron-right" size={14} />
            </div>
          </StyledPackageBanner>
        </StyledPackageBannerContainer>
      ),
    [history, packageLoading, packages, theme.primary_5, translate],
  )
  const handleTabView = useMemo(
    () =>
      tabViewVisible ? (
        <GiftShopExploreTabView
          selectedTab="collection"
          rightElement={mode === 'website' ? handleRenderPackageBanner : <></>}
        />
      ) : (
        <></>
      ),
    [handleRenderPackageBanner, mode, tabViewVisible],
  )

  const handleClickItem = useCallback(
    (item: GiftShopContentData) => {
      selectMode
        ? setSelect((previous) =>
            previous.includes(item)
              ? previous.filter((value) => value.content_id !== item.content_id)
              : [...previous, item],
          )
        : history.push('giftshop_collection_detail', {
            data: [...data],
            selectedItemId: item.content_id,
          })
    },
    [data, history, selectMode, setSelect],
  )

  const handleRenderEmpty = useMemo(
    () => (
      <StyledEmptyContainer>
        <GiftShopRootEmpty
          image={emptyImg}
          imageAlt={emptyImg}
          title={translate('giftShop:collectionEmptyTitle')}
          message={translate('giftShop:collectionEmptyDesc')}
        />
      </StyledEmptyContainer>
    ),
    [emptyImg, translate],
  )

  return (
    <>
      {handleTabView}
      {mode !== 'website' && handleRenderPackageBanner}
      {mode === 'website' && <GiftShopCollectionResendApprovalBanner />}
      <StyledMasonryContainer>
        <GiftShopTemplateMasonry
          cache="collection"
          showResBadge
          loadData={handleLoadData}
          onClickItem={handleClickItem}
          listEmptyElement={handleRenderEmpty}
          scrollbar={false}
          toggleSelect={selectMode}
          selected={select}
          stateData={stateCollectionData}
          onSelectRule={() => true}
        />
      </StyledMasonryContainer>
      {mode !== 'website' && <GiftShopCollectionResendApprovalBanner />}
    </>
  )
}
